import { createContext, useState, useEffect, useContext, useRef } from "react";

//Context
import { UserContext } from "../Contexts/UserContext";

//Fetching
import { useLazyQuery, useMutation } from "@apollo/client";
import { ACTIVE_INCIDENT_QUERY } from "../Queries/ActiveIncidentQuery";
import { AVAILABLE_OFFICERS_QUERY } from "../Queries/AvailableOfficersQuery";
import { SET_AVAIL_OFFICER } from "../Mutations/SetAvailOfficer";

//Actions
import { AVAILABLE_OFFICER_ACTIONS } from "../Actions/AvailableOfficerActions";
import { ACTIVE_INCIDENTS_ACTIONS } from "../Actions/ActiveIncidentsActions";

//utils
import { parseObjectArrayIdString } from "../utils/utils";
import { useImmerReducer } from "use-immer";
import { INACTIVE_INCIDENT_QUERY } from "../Queries/InactiveIncidentPaginatedQuery";
import { dashboardReducerValidator } from "./DashboardContextValidators";
import * as Sentry from "@sentry/react";
import { getInactiveIncidentVariables } from "../utils/dashboardQueryUtils";
import { extractGraphqlArrayPayload } from "../utils/arrayPayloadUtils";

export const DashboardContext = createContext();

const handleValidationError = validation => {
  if (import.meta.env.DEV) {
    alert(validation.error);
  } else {
    Sentry.captureException(new Error(validation.error), {
      extra: validation.details,
    });
  }
  console.error(validation);
};

const {
  SET_STATE,
  ADD_NEW_INCIDENT,
  REMOVE_ACTIVE_INCIDENT,
  UPDATE_OIS,
  ADD_OIS_TO_INCIDENT,
  UPDATE_DEPT_ATTRB,
  ADD_OR_UPDATE_ACTIVE_INCIDENT,
  REPLACE_ACTIVE_INCIDENT,
  UPDATE_ACTIVE_INCIDENT_PROPERTY,
  REMOVE_OIS_FROM_ACTIVE_INCIDENT_TABLE,
  UPDATE_OIS_DASH_MULTI_FIELD,
} = ACTIVE_INCIDENTS_ACTIONS;

export const activeIncidentReducer = (state, action) => {
  const validation = dashboardReducerValidator(action.type, action.payload);

  if (!validation.valid) {
    handleValidationError(validation);
    return state;
  }

  switch (action.type) {
    case SET_STATE:
      return { activeIncidents: action.payload };

    case ADD_NEW_INCIDENT:
      return void {
        incidents: state.activeIncidents.unshift({ ...action.payload }),
      };

    case REMOVE_ACTIVE_INCIDENT:
      return {
        activeIncidents: [
          ...state.activeIncidents.filter(i => i.id !== action.payload),
        ],
      };

    case ADD_OIS_TO_INCIDENT:
      if (
        action.payload.incidentIndex >= 0 &&
        state.activeIncidents[action.payload.incidentIndex] ===
          action.payload.incidentId
      ) {
        if (
          Array.isArray(
            state.activeIncidents[action.payload.incidentIndex]
              ?.officerIncidentSpecifics,
          )
        ) {
          return void {
            activeIncidents: state.activeIncidents[
              action.payload.incidentIndex
            ].officerIncidentSpecifics.push(action.payload.ois),
          };
        } else {
          return void {
            activeIncidents: (state.activeIncidents[
              action.payload.incidentIndex
            ].officerIncidentSpecifics = [action.payload.ois]),
          };
        }
      } else {
        return void {
          activeIncidents: state.activeIncidents.map(i => {
            if (i.id === action.payload.incidentId) {
              let updatedIncident = Object.assign({}, i);
              updatedIncident.officerIncidentSpecifics.push(action.payload.ois);
              return updatedIncident;
            } else {
              return i;
            }
          }),
        };
      }
    case REMOVE_OIS_FROM_ACTIVE_INCIDENT_TABLE:
      if (action.payload.incidentIndex >= 0 && action.payload.oisIndex >= 0) {
        state.activeIncidents[
          action.payload.incidentIndex
        ]?.officerIncidentSpecifics?.splice(action.payload.oisIndex, 1);
        return;
      } else if (
        state.activeIncidents.find(
          ai =>
            ai.id === action.payload.incidentId ||
            ai.uuid === action.payload.incidentId,
        )?.officerIncidentSpecifics?.length > 1
      ) {
        state.activeIncidents = state.activeIncidents.map(a => {
          if (
            a.id === action.payload.incidentId ||
            a.uuid === action.payload.incidentId
          ) {
            a.officerIncidentSpecifics =
              a?.officerIncidentSpecifics?.filter(
                ois =>
                  ois.id !== action.payload.oisId &&
                  ois.uuid !== action.payload.oisId,
              ) || [];
          }
          return a;
        });
        return;
      } else {
        state.activeIncidents = [
          ...state.activeIncidents.filter(
            i =>
              i.id !== action.payload.incidentId ||
              (!!i.uuid && i.uuid !== action.payload.incidentId),
          ),
        ];
        return;
      }

    case UPDATE_OIS_DASH_MULTI_FIELD: {
      const MF_incidentIndex = state.activeIncidents.findIndex(
        incident =>
          incident.id?.toString() === action.payload.incidentId?.toString() ||
          incident.uuid?.toString() === action.payload.incidentId?.toString(),
      );
      const MF_oisIndex =
        MF_incidentIndex > -1
          ? state.activeIncidents[
              MF_incidentIndex
            ].officerIncidentSpecifics.findIndex(
              ois =>
                ois.id?.toString() === action.payload.oisId?.toString() ||
                ois.id?.toString() === action.payload.oisUuid?.toString(),
            )
          : -1;
      if (MF_incidentIndex > -1 && MF_oisIndex > -1) {
        return void {
          activeIncidents: (state.activeIncidents[
            MF_incidentIndex
          ].officerIncidentSpecifics[MF_oisIndex] = {
            ...state.activeIncidents[MF_incidentIndex].officerIncidentSpecifics[
              MF_oisIndex
            ],
            ...action.payload.updatedOis,
          }),
        };
      } else {
        return state;
      }
    }

    case UPDATE_OIS:
      if (
        action.payload.incidentIndex >= 0 &&
        action.payload.oisIndex >= 0 &&
        action.payload.property
      ) {
        return void {
          activeIncidents: (state.activeIncidents[
            action.payload.incidentIndex
          ].officerIncidentSpecifics[action.payload.oisIndex][
            action.payload.property
          ] = action.payload.value),
        };
      } else {
        return void {
          activeIncidents: state.activeIncidents.map(i => {
            if (
              i.id === action.payload.incidentId ||
              i.uuid === action.payload.incidentId
            ) {
              i.officerIncidentSpecifics.map(ois => {
                if (
                  ois.id === action.payload.oisId ||
                  ois.uuid === action.payload.oisId
                ) {
                  ois[action.payload.property] = action.payload.value;
                }
                return ois;
              });
              return i;
            } else {
              return i;
            }
          }),
        };
      }

    case UPDATE_DEPT_ATTRB:
      return void {
        activeIncidents: state.activeIncidents.map(i => {
          if (i.id === action.payload.incidentId) {
            i.officerIncidentSpecifics.map(ois => {
              if (ois.id === action.payload.oisId) {
                // Fix: Ensure deptAttribute is an object
                if (
                  !ois.officer.deptAttribute ||
                  typeof ois.officer.deptAttribute !== "object"
                ) {
                  ois.officer.deptAttribute = {};
                }
                ois.officer.deptAttribute[action.payload.property] =
                  action.payload.value;
              }
              return ois;
            });
            return i;
          } else {
            return i;
          }
        }),
      };

    case UPDATE_ACTIVE_INCIDENT_PROPERTY:
      if (action.payload.accessor) {
        return void {
          activeIncidents: state.activeIncidents.map(i => {
            if (
              i.id === action.payload.incidentId ||
              i.uuid === action.payload.incidentId
            ) {
              i[action.payload.accessor][action.payload.property] =
                action.payload.value;
              if (action.payload.nextProperty) {
                i[action.payload.accessor][action.payload.nextProperty] =
                  action.payload.nextValue;
              }
              return i;
            } else {
              return i;
            }
          }),
        };
      } else {
        return {
          activeIncidents: state.activeIncidents.map(i => {
            if (
              i.id === action.payload.incidentId ||
              i.uuid === action.payload.incidentId
            ) {
              return { ...i, [action.payload.property]: action.payload.value };
            } else {
              return i;
            }
          }),
        };
      }

    case REPLACE_ACTIVE_INCIDENT:
      //passing the entire incidents
      return {
        activeIncidents: state.activeIncidents.map(i => {
          if (i.id === action.payload.id || i.uuid === action.payload.uuid) {
            return action.payload;
          } else {
            return i;
          }
        }),
      };

    case ADD_OR_UPDATE_ACTIVE_INCIDENT:
      return void {
        activeIncidents: [...state.activeIncidents, action.payload],
      };

    default:
      console.error("No matching action type in activeIncidentReducer");
      return state;
  }
};

const { UPDATE_AVAILABLE_OFFICER } = AVAILABLE_OFFICER_ACTIONS;
export const availableOfficerReducer = (state, action) => {
  const validation = dashboardReducerValidator(action.type, action.payload);

  if (!validation.valid) {
    handleValidationError(validation);
    return state;
  }

  switch (action.type) {
    case SET_STATE:
      return { availableOfficers: action.payload };

    case UPDATE_AVAILABLE_OFFICER:
      return void {
        availableOfficers: state.availableOfficers.map(officer => {
          if (
            officer?.id?.toString() === action.payload.officerId?.toString()
          ) {
            if (
              !officer.deptAttribute ||
              typeof officer.deptAttribute !== "object"
            ) {
              officer.deptAttribute = {};
            }
            officer.deptAttribute[action.payload.property] =
              action.payload.value;
            return officer;
          } else {
            return officer;
          }
        }),
      };

    default:
      return state;
  }
};

const DashboardContextProvider = props => {
  const { user, responseErrorHandler } = useContext(UserContext);
  const [{ activeIncidents }, activeIncidentDispatch] = useImmerReducer(
    activeIncidentReducer,
    { activeIncidents: [] },
  );
  const [{ availableOfficers }, availableOfficerDispatch] = useImmerReducer(
    availableOfficerReducer,
    { availableOfficers: [] },
  );
  const [showActiveIncidents, setShowActiveIncidents] = useState(true);
  const _DashboardContextIsMounted = useRef(true); // Initial value _isMounted = true
  const _dashboardIsMounted = useRef(true); // Initial value _dashboardIsMounted = true
  /*******This is only for development. The immer state isn't visible in the Components inspector so it's being mirrored below. Delete when done.*******/
  // const [mirror, setMirror] = useState(activeIncidents)
  // useEffect(() => { setMirror(activeIncidents) }, [activeIncidents])
  // const [officerMirror, setOfficerMirror] = useState(availableOfficers)
  // useEffect(() => { setOfficerMirror(availableOfficers) }, [availableOfficers])
  /*******Delete when done.*******/

  const [
    getActiveIncidents,
    {
      loading: activeIncidentsLoading,
      error: activeIncidentsError,
      refetch: activeIncidentsRefetch,
      called: activeIncidentsCalled,
      data: activeIncidentsData,
    },
  ] = useLazyQuery(ACTIVE_INCIDENT_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id, homeOriId: user?.homeOriId },
    fetchPolicy: "network-only",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const payload = extractGraphqlArrayPayload(data, "activeIncidents");
        if (Array.isArray(payload)) {
          activeIncidentDispatch({
            type: SET_STATE,
            payload,
          });
        }
      }
    },
  });

  const [
    getAvailableOfficers,
    {
      loading: availableOfficersLoading,
      error: availableOfficersError,
      refetch: availableOfficersRefetch,
      called: availableOfficersCalled,
    },
  ] = useLazyQuery(AVAILABLE_OFFICERS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { oriIds: user?.selectedOris, originatorId: user?.id },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        const payload = extractGraphqlArrayPayload(data, "availableOfficers");
        if (Array.isArray(payload)) {
          availableOfficerDispatch({
            type: SET_STATE,
            payload,
          });
        }
      }
    },
  });

  const [
    getInactiveIncidents,
    {
      data: inactiveIncidentsData,
      loading: inactiveIncidentsLoading,
      error: inactiveIncidentsError,
    },
  ] = useLazyQuery(INACTIVE_INCIDENT_QUERY, {
    context: { serializationKey: "incidentQueue" },
    variables: { originatorId: user?.id },
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
      }
    },
  });

  //Mutations
  const [setAvailOfficer] = useMutation(SET_AVAIL_OFFICER, {
    variables: { input: { originatorId: user?.id } },
    onCompleted: data => data && responseErrorHandler(data),
  });

  useEffect(() => {
    if (
      user?.selectedOrisObjs &&
      user?.homeOriId &&
      user?.id &&
      _dashboardIsMounted
    ) {
      getActiveIncidents({
        variables: {
          originatorId: user?.id,
          noSessionReset: true,
          oriIds: parseObjectArrayIdString(user?.selectedOrisObjs),
          homeOriId: user?.homeOriId,
        },
      });
      getAvailableOfficers({
        variables: {
          originatorId: user?.id,
          noSessionReset: true,
          oriIds: parseObjectArrayIdString(user?.selectedOrisObjs),
          homeOriId: user?.homeOriId,
        },
      });
    }
  }, [
    user?.selectedOrisObjs,
    user?.homeOriId,
    user?.id,
    getActiveIncidents,
    getAvailableOfficers,
    user?.onDuty,
  ]);

  //Setting polling of active incidents until the subscription server is in use

  // useEffect(() => {
  //   if (import.meta.env.VITE_USE_POLLING.toLowerCase() === "true") {
  //     const interval = setInterval(() => {
  //       if (onlineState && user?.isAuthenticated) {
  //         getAvailableOfficers({ variables: { oriIds: parseObjectArrayIdString(user?.selectedOrisObjs), homeOriId: user?.homeOriId } })
  //       }
  //     }, 10000);
  //     return () => { clearInterval(interval) }
  //   }
  // });

  useEffect(() => {
    return () => {
      // ComponentWillUnmount in Class Component
      _DashboardContextIsMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!showActiveIncidents) {
      user?.id &&
        getInactiveIncidents({
          variables: getInactiveIncidentVariables(user, 10, 1),
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showActiveIncidents, user?.selectedOrisObjs, user?.id]);

  let indexedDb = props.indexedDb;

  return (
    <DashboardContext.Provider
      value={{
        activeIncidents,
        activeIncidentDispatch,
        availableOfficers,
        availableOfficerDispatch,
        activeIncidentsLoading,
        activeIncidentsError,
        activeIncidentsRefetch,
        activeIncidentsCalled,
        activeIncidentsData,
        availableOfficersLoading,
        availableOfficersError,
        availableOfficersRefetch,
        availableOfficersCalled,
        setAvailOfficer,
        indexedDb,
        showActiveIncidents,
        setShowActiveIncidents,
        _DashboardContextIsMounted,
        getInactiveIncidents,
        inactiveIncidentsData,
        inactiveIncidentsLoading,
        inactiveIncidentsError,
        getActiveIncidents,
        getAvailableOfficers,
        _dashboardIsMounted,
      }}
    >
      {props.children}
    </DashboardContext.Provider>
  );
};

export default DashboardContextProvider;
